/* eslint-disable camelcase, @typescript-eslint/naming-convention */
/**
 * Zod schemas for PageContext input validation.
 *
 * Two schemas mirror the two wire versions:
 *   - pageContextV1Schema: camelCase v1 shape from Rails / web portal callers.
 *   - pageContextV2Schema: snake_case v2 shape from iOS / v2 producers.
 *
 * parsePageContext() runs the appropriate schema based on schema_version /
 * schemaVersion. On failure it logs a warning and returns undefined — the
 * widget never throws on bad context.
 *
 * The v2 schema is structural-only; it does NOT perform business-logic checks
 * (e.g. attrs byte-budget, attrs pattern). Those live in the orchestrator
 * Pydantic layer where they belong.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// v1 schema — camelCase, permissive on pageType (string union is wide).
// schemaVersion is optional and defaults to 1 on the wire.
// ---------------------------------------------------------------------------
const pageContextV1Schema = z.object({
    schemaVersion: z.number().optional(),
    pageType: z.string().min(1),
    pagePath: z.string().nullish(),
    buildingId: z.number().int().nullish(),
    buildingName: z.string().nullish(),
    territoryId: z.number().int().nullish(),
    inspectionId: z.number().int().nullish(),
    inspectionLabel: z.string().nullish(),
    inspectionSeriesId: z.number().int().nullish(),
    workOrderId: z.number().int().nullish(),
    workOrderNumber: z.string().nullish(),
    proposalId: z.number().int().nullish(),
    reportId: z.number().int().nullish(),
    accountId: z.number().int().nullish(),
    deficiencyId: z.number().int().nullish(),
    contactId: z.number().int().nullish(),
    invoiceId: z.number().int().nullish(),
    deviceId: z.number().int().nullish(),
    deviceType: z.string().nullish()
});
// ---------------------------------------------------------------------------
// v2 schema — snake_case, strict schema_version: 2.
// live_entities capped at 8 (matching orchestrator Pydantic max_length=8).
// attrs values are scalars only — no nested objects/arrays.
// ---------------------------------------------------------------------------
const attrValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const liveEntitySchema = z.looseObject({
    entity_type: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/),
    source_view: z.string().max(128).nullish(),
    id: z.number().int().positive().nullish(),
    label: z.string().max(200).nullish(),
    attrs: z.record(z.string(), attrValueSchema).nullish()
});
// An active-filter value: scalar (`=`), array of scalars (`IN`, multi-select),
// or a { from, to } date range (`BETWEEN`). Mirrors the orchestrator ListContext
// active_filters union and listContextFromGridState's ActiveFilterValue.
const dateRangeFilterSchema = z.object({
    from: z.string().nullable(),
    to: z.string().nullable()
});
const activeFilterValueSchema = z.union([
    attrValueSchema,
    z.array(z.union([z.string(), z.number(), z.boolean()])),
    dateRangeFilterSchema
]);
// Channel-3 listing summary (server snapshot or live overlay from the grids).
// Loose so future fields pass through; mirrors the orchestrator ListContext.
const listContextSchema = z.looseObject({
    total_count: z.number().int().nullish(),
    filtered_count: z.number().int().nullish(),
    active_filters: z.record(z.string(), activeFilterValueSchema).nullish(),
    filters_complete: z.boolean().optional(),
    sort: z.string().max(64).nullish(),
    page: z.number().int().nullish(),
    page_size: z.number().int().nullish(),
    visible_ids: z.array(z.number().int()).nullish()
});
const pageContextV2Schema = z.looseObject({
    schema_version: z.literal(2),
    page_type: z.string().min(1),
    route: z.string().max(200).nullish(),
    surface: z.enum(['web', 'ios_webview']).nullish(),
    // Channel-1 IDs
    building_id: z.number().int().nullish(),
    territory_id: z.number().int().nullish(),
    inspection_id: z.number().int().nullish(),
    inspection_series_id: z.number().int().nullish(),
    work_order_id: z.number().int().nullish(),
    proposal_id: z.number().int().nullish(),
    report_id: z.number().int().nullish(),
    account_id: z.number().int().nullish(),
    deficiency_id: z.number().int().nullish(),
    contact_id: z.number().int().nullish(),
    invoice_id: z.number().int().nullish(),
    visit_id: z.number().int().nullish(),
    alarm_system_id: z.number().int().nullish(),
    control_panel_id: z.number().int().nullish(),
    // Channel-2 live entities (≤8 items)
    live_entities: z.array(liveEntitySchema).max(8).nullish(),
    // Channel-3 listing summary (counts/filters/sort/page/visible IDs)
    list_context: listContextSchema.nullish()
});
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Validate a raw PageContext value.
 *
 * - If the value has `schema_version === 2` (snake_case v2), it is validated
 *   against the v2 Zod schema and returned as-is for passthrough.
 * - Otherwise (`schemaVersion` present/absent, or `schema_version === 1`),
 *   it is validated against the v1 Zod schema.
 * - On any validation failure: logs a console.warn and returns undefined.
 *   The widget must drop invalid context rather than crashing.
 *
 * @param raw - Unvalidated input from mountChatWidget options or updatePageContext.
 * @returns The validated PageContext, or undefined if invalid.
 */
export function parsePageContext(raw) {
    if (raw === undefined || raw === null) {
        return undefined;
    }
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        console.warn('[AIChatWidget] pageContext must be an object — dropping invalid context', raw);
        return undefined;
    }
    const input = raw;
    // Detect v2 by snake_case schema_version field.
    // On success, return the ORIGINAL object (not result.data) so all keys —
    // including any future additive fields from iOS — pass through unchanged.
    // The orchestrator's Pydantic layer (extra="forbid") is the source of truth
    // for which fields are allowed; the widget is just a pipe.
    if (input.schema_version === 2) {
        const result = pageContextV2Schema.safeParse(input);
        if (result.success) {
            return input;
        }
        console.warn('[AIChatWidget] Invalid v2 pageContext — dropping context', result.error.issues);
        return undefined;
    }
    // v1 path (schema_version 1 or omitted, camelCase shape)
    const result = pageContextV1Schema.safeParse(input);
    if (result.success) {
        return result.data;
    }
    console.warn('[AIChatWidget] Invalid v1 pageContext — dropping context', result.error.issues);
    return undefined;
}
